import { useState } from "react";
import Box from "@mui/material/Box";
import { BUTTON_ADD_KEYWORDS } from "../../i18n/keysTranslations";
import { useController } from "react-hook-form";
import Typography from "@mui/material/Typography";
import { getReactHookFormNestedError } from "../../utils/errors";
import KeywordsTable from "./Keywords/KeywordsTable";
import KeywordModal from "./Keywords/KeywordModal";
import Button from "../Buttons/Button";
import { useTranslationApp } from "../../lib/i18next";
import { ChipAIGeneratedSection } from "../Chip/ChipAIGenerated";
import { getKeywordMatchType } from "../../utils/string";
import KeywordsModal from "./Keywords/KeywordsModal";

const KeywordsGoogleContainer = ({
  label,
  name,
  disabled = false,
  control,
  errors,
  rules,
  isNegativeKeywords = false,
  afterTitle,
}) => {
  const [modalKeywordOpen, setModalKeywordOpen] = useState(false);
  const [modalKeywordsOpen, setModalKeywordsOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const { t } = useTranslationApp();

  const {
    field: { value: keywords = [], onChange: keywordsOnChange },
  } = useController({
    name,
    control,
    rules,
  });

  const error = getReactHookFormNestedError(errors, name);
  const sectionName = typeof name === "string" ? name.split(".").pop() : "";
  const aiBasePath =
    sectionName && name.endsWith(`.${sectionName}`)
      ? name.slice(0, -(sectionName.length + 1))
      : "";
  const showAIChip =
    sectionName === "keywords" || sectionName === "keywordsNegative";

  const onAddKeywords = (newKeywordsAdded) => {
    const newKeywords = [...keywords];
    newKeywords.unshift(...newKeywordsAdded);
    keywordsOnChange(newKeywords);
  };

  const onEditKeyword = (keyword) => {
    const newKeywords = [...keywords];
    const index = newKeywords.indexOf(editingKeyword.original);
    if (index !== -1) {
      newKeywords[index] = keyword;
      keywordsOnChange(newKeywords);
    }
    setEditingKeyword(null);
  };

  const onDeleteKeywords = (keywordsToDelete) => {
    const newKeywords = keywords.filter((k) => !keywordsToDelete.includes(k));
    keywordsOnChange(newKeywords);

    return true;
  };

  const openEditModal = (keyword) => {
    setEditingKeyword(keyword);
    setModalKeywordOpen(true);
  };

  const onCloseModal = () => {
    setModalKeywordOpen(false);
    setEditingKeyword(null);
  };

  const keywordsFormatted = keywords.map((keyword) => ({
    ...getKeywordMatchType(keyword),
    original: keyword,
  }));

  return (
    <Box id={name} sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mt: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, fontSize: "18px" }}
              >
                {label}
              </Typography>
              {showAIChip ? (
                <ChipAIGeneratedSection
                  basePath={aiBasePath}
                  section={sectionName}
                />
              ) : null}
            </Box>
            {afterTitle && <Box sx={{ mb: 1 }}>{afterTitle}</Box>}
          </Box>
        </Box>
        {!disabled && (
          <Button color="secondary" onClick={() => setModalKeywordsOpen(true)}>
            {t(BUTTON_ADD_KEYWORDS)}
          </Button>
        )}
      </Box>

      <KeywordsTable
        isNegativeKeywords={isNegativeKeywords}
        keywords={keywordsFormatted}
        onEdit={openEditModal}
        onDelete={onDeleteKeywords}
        disabled={disabled}
      />

      {modalKeywordOpen && (
        <KeywordModal
          modalOpen={modalKeywordOpen}
          onCloseModal={onCloseModal}
          onSubmit={onEditKeyword}
          currentKeywords={keywords}
          initialData={editingKeyword}
        />
      )}

      {modalKeywordsOpen && (
        <KeywordsModal
          modalOpen={modalKeywordsOpen}
          onCloseModal={() => setModalKeywordsOpen(false)}
          onSubmit={onAddKeywords}
          currentKeywords={keywords}
        />
      )}

      {error?.message && (
        <Typography variant="caption" color="error" name={name}>
          {error.message}
        </Typography>
      )}
    </Box>
  );
};

export default KeywordsGoogleContainer;
